import { first } from '@execonline-inc/collections';
import { toTask } from '@execonline-inc/maybe-adapter';
import { directUpload, validateFile, withScrubbedName } from '@execonline-inc/file-upload.private';
import { noop } from '@kofno/piper';
import { fromEmpty, fromNullable, just, Maybe, nothing } from 'maybeasy';
import { ChangeEvent } from 'react';
import { Task } from 'taskarian';
import { Link } from '../../../../../../../Resource/Types';
import { TPlainTextKey } from '../../../../../../../Translations';
import {
  collectionLogoRules,
  directUploadConfig,
  DirectUploadFailure,
  logoErrorMessage,
} from '../../../../../../../Uploads/DirectUploadConfig';
import { UploadImageStore } from '../UploadImageStore';

/**
 * What the learning collection form submits for a logo the user just
 * uploaded. The browser uploads the file straight to S3 and the server
 * decides the key, so `objName` is that key.
 *
 * EnterprisePortal::UseCase::CpPersistenceService reads these four fields when
 * it copies the object into the content library.
 */
export interface FileMetaData {
  assetType: string;
  extension: string;
  fileName: string;
  objName: string;
}

interface NoFileFound {
  kind: 'no-file-found';
}

export type FileUploadErrors = NoFileFound | DirectUploadFailure;

const noFileFound = (): NoFileFound => ({ kind: 'no-file-found' });

/** The purpose in DirectUpload::Purpose whose rules the server applies. */
const LOGO_PURPOSE = 'learning_collection_logo';

/** The asset type CpPersistenceService expects for a collection logo. */
const LOGO_ASSET_TYPE = 'image';

export const handleDelete = (
  setPreviewUrl: (url: Maybe<Link>) => void,
  store: UploadImageStore,
) => {
  store.deleteImage();
  setPreviewUrl(nothing());
};

const getFile = (files: FileList | null): Maybe<File> =>
  fromNullable(files)
    .andThen((thing: FileList) => fromEmpty([...thing]))
    .andThen(first);

export const fetchFile = (files: FileList | null): Task<FileUploadErrors, File> =>
  toTask(noFileFound(), getFile(files)).map(withScrubbedName);

// The server applies the same rules before it signs, so this only exists to
// tell the user sooner.
export const checkFile = (file: File): Task<FileUploadErrors, File> =>
  validateFile(file, collectionLogoRules).cata<Task<FileUploadErrors, File>>({
    Err: (error) => Task.fail<FileUploadErrors, File>(error),
    Ok: (valid) => Task.succeed<FileUploadErrors, File>(valid),
  });

const localPreviewUrl = (file: File): Task<FileUploadErrors, string> =>
  new Task<FileUploadErrors, string>((reject, resolve) => {
    try {
      resolve(URL.createObjectURL(file));
    } catch (e) {
      reject({ kind: 'unexpected-response', filename: file.name, message: String(e) });
    }
    return noop;
  });

// The key the server chose carries everything the form has to submit. Its
// last segment is the filename and its extension is the one the server
// allowed, so neither has to be read back off the picked file.
const toFileMetaData = (key: string): FileMetaData => {
  const fileName = key.split('/').pop() ?? key;
  const dot = fileName.lastIndexOf('.');

  return {
    assetType: LOGO_ASSET_TYPE,
    extension: dot >= 0 ? fileName.slice(dot) : '',
    fileName,
    objName: key,
  };
};

const uploadLogo = (config: {
  file: File;
  uploadLink: Link;
}): Task<FileUploadErrors, FileMetaData> =>
  directUploadConfig(config.uploadLink, LOGO_PURPOSE)
    .mapError<FileUploadErrors>((error) => error)
    .andThen((uploadConfig) =>
      directUpload(config.file, uploadConfig).mapError<FileUploadErrors>((error) => error),
    )
    .andThen((upload) =>
      upload.kind === 'key-upload'
        ? Task.succeed<FileUploadErrors, FileMetaData>(toFileMetaData(upload.key))
        : Task.fail<FileUploadErrors, FileMetaData>({
            kind: 'unexpected-response',
            filename: config.file.name,
            message: 'the logo upload did not come back with a key',
          }),
    );

export const handleImageChange =
  (
    store: UploadImageStore,
    uploadLink: Link,
    previewUrl: Maybe<Link>,
    setPreviewUrl: (url: Maybe<Link>) => void,
    onSuccess?: (fileMetaData: FileMetaData) => void,
  ) =>
  (event: ChangeEvent<HTMLInputElement>) => {
    store.loading();
    Task.succeed<FileUploadErrors, {}>({})
      .assign('uploadLink', Task.succeed<FileUploadErrors, Link>(uploadLink))
      .assign('file', fetchFile(event.target.files).andThen(checkFile))
      .assign('fileURL', ({ file }) => localPreviewUrl(file))
      .assign('fileMetaData', uploadLogo)
      .fork(
        (error) => store.error(handleErrorMessage(error)),
        ({ fileURL, fileMetaData }) => {
          // Call onSuccess callback BEFORE store.ready() to ensure it runs
          // before MobX observers trigger re-renders that might unmount components
          onSuccess?.(fileMetaData);
          handleSuccess(fileURL, fileMetaData, previewUrl, store, setPreviewUrl);
        },
      );
  };

export const handleErrorMessage = (error: FileUploadErrors): TPlainTextKey => {
  switch (error.kind) {
    case 'no-file-found':
      return 'File url error';
    default:
      return logoErrorMessage(error);
  }
};

export const handleSuccess = (
  url: string,
  fileMetaData: FileMetaData,
  previewUrl: Maybe<Link>,
  store: UploadImageStore,
  setPreviewUrl: (url: Maybe<Link>) => void,
) => {
  const getImageHref = previewUrl
    .map((link) => ({ ...link, href: url }))
    .getOrElse(() => ({
      href: url,
      method: 'get',
      rel: 'learning-collection-logo',
      type: 'application/json',
    }));

  setPreviewUrl(just(getImageHref));
  store.ready(just(fileMetaData));
};
